/**
 * InstantDB client bootstrap — conditional and idempotent.
 *
 * - When `VITE_INSTANT_APP_ID` is set, `getDb()` returns a live `init`-ed
 *   client. Subsequent calls return the same singleton.
 * - When the env var is empty or unset, `getDb()` returns `null` and the
 *   rest of the app falls back to the localStorage-backed mock layer
 *   (usePersistentMockState's existing demoStore path).
 *
 * This conditional init is the linchpin of the dual-backed strategy: pages
 * and hooks don't need to know which backend is active. They call into the
 * hook layer, which checks `isInstantDbEnabled()` once and routes accordingly.
 *
 * The app ID is public-by-design (security is enforced via instant.perms.ts
 * server-side), so reading it from the client env is fine.
 *
 * Recommended `queryCacheLimit` for an ERP with 40+ entities and dozens of
 * list/detail views: 150. Each unique useQuery counts as one cache entry.
 */

import { init } from "@instantdb/react";

import schema from "../../../instant.schema";

/**
 * Type of the configured InstantDB client. We use a simple alias because:
 *  - `ReturnType<typeof init<typeof schema>>` is invalid TS (no generics on
 *    `typeof` of a function).
 *  - Schema-aware typing on db.useQuery / db.tx isn't usable in practice
 *    because we route through dynamic entity names — every call site casts
 *    through `(db.xxx as any)` anyway.
 * The unknown index lets us call any member without TS errors.
 */
export type BizopDb = ReturnType<typeof init>;

/** Singleton db instance, populated on first `getDb()` call. */
let _db: BizopDb | null = null;

/**
 * Read the configured InstantDB app ID. Returns undefined if missing/empty.
 * Treats whitespace-only and obvious placeholders ("your-app-id") as unset.
 */
export function getInstantAppId(): string | undefined {
  const raw = import.meta.env.VITE_INSTANT_APP_ID;
  if (!raw) return undefined;
  const trimmed = String(raw).trim();
  if (!trimmed) return undefined;
  if (trimmed.includes("your-app-id") || trimmed.includes("xxxxx")) {
    return undefined;
  }
  return trimmed;
}

/** Boolean shortcut — does this build have InstantDB wired up? */
export function isInstantDbEnabled(): boolean {
  return getInstantAppId() !== undefined;
}

/**
 * Return the singleton InstantDB client, or `null` if no app ID is configured.
 *
 * Safe to call repeatedly. The underlying SDK maintains a single WebSocket
 * per appId, so even multiple `init()` calls would be idempotent.
 */
export function getDb(): BizopDb | null {
  if (_db) return _db;
  const appId = getInstantAppId();
  if (!appId) {
    if (import.meta.env.DEV) {
      // Only warn once per session — getDb is called frequently
      if (!(globalThis as { __bizopDbWarned?: boolean }).__bizopDbWarned) {
        console.info(
          "[bizop-admin] VITE_INSTANT_APP_ID is empty. Falling back to localStorage-backed mock data. Set the env var to enable InstantDB sync.",
        );
        (globalThis as { __bizopDbWarned?: boolean }).__bizopDbWarned = true;
      }
    }
    return null;
  }

  // Build init options. IMPORTANT: we only set apiURI/websocketURI when
  // the env vars actually have a value — passing `undefined` causes the
  // SDK to stringify it ("undefined") and construct a broken URL like
  // `wss://your-domain.com/undefined?app_id=...`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initOptions: Record<string, any> = {
    appId,
    schema,
    queryCacheLimit: 150,
    useDateObjects: true,
    devtool: import.meta.env.DEV
      ? { position: "bottom-right", allowedHosts: ["localhost"] }
      : false,
  };

  const apiUri = import.meta.env.VITE_INSTANT_API_URI;
  if (typeof apiUri === "string" && apiUri.length > 0) {
    initOptions.apiURI = apiUri;
  }
  const wsUri = import.meta.env.VITE_INSTANT_WS_URI;
  if (typeof wsUri === "string" && wsUri.length > 0) {
    initOptions.websocketURI = wsUri;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _db = init(initOptions as any);

  return _db;
}

/**
 * For tests / dev tools: reset the singleton. Not used in production code.
 */
export function _resetDb(): void {
  _db = null;
}
